import 'regenerator-runtime/runtime';
import { SagaIterator, Task } from 'redux-saga';
import { call, delay, fork, join, put, race, select, take, takeLeading } from 'redux-saga/effects';
import dayjs from 'dayjs';
import { AnyAction } from 'redux';
import {
  GlobalAction,
  GlobalActions,
  GlobalEventDispatcher,
} from '@ps20-frontend/shop-integration-redux';
import { App, AppSubroute, TierartEnum } from '@ps20-frontend/frontend-models';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { DATEFORMAT_SHORT_ISO } from '@ps20-frontend/utils';
import { IAppState } from '../appState';
import { hasAnimalOfType, isFormValid } from '../models/formValidation';
import { childOfKey } from '../models/childKeys';
import { animalValidationContext } from '../models/tier';
import { tkvProdukttabelle } from '../models/tkvProdukttabelle';
import { TgvTier, TgvVorschaedenState } from '../models/tiergruppe.model';
import {
  hasLoadedProdukt,
  keepsTkvV2Produkt,
  toThhHundState,
  toThhPferdState,
  toTkvV2State,
} from '../mappers/childStates';
import { TgvActions } from '../tgvActions';
import { selectTakeOverRunning } from '../selector/transientState.selector';
import { markAndDeleteKeys } from './markAndDeleteKeys';
import { deferOrphanedOffersSaga } from './orphanedOffersSaga';
import { SHOP_RUN_WAIT_MS } from './pendingDeletionWait';
import { findStoreState, sharedDispatcher } from './sharedDispatcher';

export const ACK_TIMEOUT_MS = 10000;
export const PENDING_DELETION_WAIT_MS = 10000;

export function* watchZumAngebot(): SagaIterator {
  yield takeLeading(TgvActions.TGV_ZUM_ANGEBOT_CLICKED, safeSaga(zumAngebotSaga, App.TGV));
}

/** Like the product tiles of the dashboard: the date that the test URL sets, else today. */
export const angebotsdatumOfShop = (): string =>
  (
    findStoreState(App.SHOP_DASHBOARD)?.['transientState'] as
      | { customAngebotsdatum?: string }
      | undefined
  )?.customAngebotsdatum ?? dayjs().format(DATEFORMAT_SHORT_ISO);

interface WarenkorbRow {
  appId: string;
  ownerAppId?: string;
}

const warenkorbRowOf = (app: App): WarenkorbRow | undefined =>
  (findStoreState(App.SHOP_DASHBOARD)?.['warenkorb'] as WarenkorbRow[] | undefined)?.find(
    angebot => angebot.appId === app,
  );

interface HealthStatusLists {
  dogCat?: unknown[];
  horse?: unknown[];
}

/**
 * The checkout loads the Gesundheitszustand list with the product request of a TKV V2 offer. The
 * two lists (Hund/Katze and Pferd) are the same for every offer, so only the first product
 * request of a type asks for it, unless the checkout has it already.
 */
const healthStatusAsker = () => {
  const lists = (
    findStoreState(App.SHOP_CHECKOUT) as
      | { tkvAngabenV2?: { healthStatusList?: HealthStatusLists } }
      | undefined
  )?.tkvAngabenV2?.healthStatusList;
  const asked = new Set<keyof HealthStatusLists>();
  return (tierart: TierartEnum): boolean => {
    const list = tierart === TierartEnum.PFERD ? 'horse' : 'dogCat';
    const loaded = (lists?.[list]?.length ?? 0) > 0 || asked.has(list);
    asked.add(list);
    return loaded;
  };
};

interface ZumAngebotContext {
  dispatcher: GlobalEventDispatcher;
  globalActions: GlobalActions;
  tgvActions: TgvActions;
  angebotsdatum: string;
  katalog: IAppState['transientState']['katalog'];
}

interface ThhOffer {
  key: keyof TgvVorschaedenState;
  app: App;
  tierart: TierartEnum;
  toState: typeof toThhHundState;
}

interface CreatedChild {
  cardId?: string;
  thh?: keyof TgvVorschaedenState;
}

type CreatedInRun = Map<string, CreatedChild>;

const THH_OFFERS: ThhOffer[] = [
  { key: 'thhHund', app: App.THH_HUND, tierart: TierartEnum.HUND, toState: toThhHundState },
  { key: 'thhPferd', app: App.THH_PFERD, tierart: TierartEnum.PFERD, toState: toThhPferdState },
];

const isOwnedStoreInitializedOf =
  (appId: App, angebotsId: string) =>
  (action: AnyAction): boolean =>
    action.type === GlobalActions.OWNED_STORE_INITIALIZED &&
    action.payload?.appId === appId &&
    action.payload?.angebotsId === angebotsId;

const leavesToDashboard = (action: AnyAction): boolean =>
  action.type === GlobalActions.NAVIGATION_REQUESTED &&
  action.payload?.appId === App.SHOP_DASHBOARD;

const restoresTgvSnapshot = (action: AnyAction): boolean =>
  action.type === GlobalActions.RESTORE_STRECKEN_SNAPSHOT && action.payload?.appId === App.TGV;

function* waitForAck(angebotsId: string): SagaIterator {
  return yield race({
    acked: take(isOwnedStoreInitializedOf(App.TKV_V2, angebotsId)),
    timeout: delay(ACK_TIMEOUT_MS),
  });
}

function* waitForPendingDeletionCleared(): SagaIterator {
  while (true) {
    const { transientState }: IAppState = yield select();
    if (transientState.children.pendingDeletion.length === 0) {
      return;
    }
    yield take([GlobalActions.PRODUKT_DELETED, TgvActions.TGV_PENDING_DELETION_CLEARED]);
  }
}

function* waitForPendingDeletionClearedOrTimeout(timeoutMs: number): SagaIterator<boolean> {
  const result = yield race({
    cleared: call(waitForPendingDeletionCleared),
    timeout: delay(timeoutMs),
  });
  return Boolean(result.timeout);
}

function* deletePendingChildrenItself(tgvActions: TgvActions): SagaIterator {
  const { transientState }: IAppState = yield select();
  const keys = [...transientState.children.pendingDeletion];
  if (keys.length === 0) {
    return;
  }
  sharedDispatcher().dispatch(new GlobalActions().produktDeleted(keys.map(childOfKey)));
  const timedOut: boolean = yield* waitForPendingDeletionClearedOrTimeout(PENDING_DELETION_WAIT_MS);
  if (!timedOut) {
    return;
  }
  const { transientState: after, tiergruppe }: IAppState = yield select();
  const stillPending = after.children.pendingDeletion;
  for (const [cardId, angebotsId] of Object.entries(tiergruppe.angebotsIds)) {
    if (stillPending.includes(angebotsId)) {
      yield put(tgvActions.angebotsIdCleared(cardId));
    }
  }
  if (stillPending.length > 0) {
    yield put(tgvActions.pendingDeletionCleared([...stillPending]));
  }
}

function* settlePendingDeletion(tgvActions: TgvActions): SagaIterator<IAppState> {
  const { transientState }: IAppState = yield select();
  if (transientState.children.pendingDeletion.length > 0) {
    const timedOut: boolean = yield* waitForPendingDeletionClearedOrTimeout(SHOP_RUN_WAIT_MS);
    if (timedOut) {
      yield call(deletePendingChildrenItself, tgvActions);
    }
  }
  return yield select();
}

function* endWithMessage(tgvActions: TgvActions, reason: string): SagaIterator<boolean> {
  yield put(tgvActions.runFailed(reason));
  return false;
}

function* deleteTkvChild(tgvActions: TgvActions, cardId: string, angebotsId: string): SagaIterator {
  yield* markAndDeleteKeys([angebotsId]);
  yield put(tgvActions.angebotsIdCleared(cardId));
}

export function* createTkvChild(
  tier: TgvTier,
  context: ZumAngebotContext,
  created: CreatedInRun,
): SagaIterator<string | null> {
  const { dispatcher, globalActions, tgvActions, angebotsdatum } = context;
  const addAction = tier.vorvertrag
    ? globalActions.addVertragToAngebote({
        ...tier.vorvertrag,
        appId: App.TKV_V2,
        angebotsdatum,
        ownerAppId: App.TGV,
      })
    : globalActions.addProduktToAngebote(App.TKV_V2, angebotsdatum, undefined, App.TGV);
  const angebotsId = addAction.payload.angebotsId;
  yield put(tgvActions.angebotsIdAssigned(tier.cardId, angebotsId));
  yield put(tgvActions.childCreatingAdded(angebotsId));
  created.set(angebotsId, { cardId: tier.cardId });
  const ack: Task = yield fork(waitForAck, angebotsId);
  dispatcher.dispatch(addAction as GlobalAction<unknown>);
  const result = yield join(ack);
  yield put(tgvActions.childCreatingRemoved(angebotsId));
  if (result.timeout) {
    created.delete(angebotsId);
    yield* deleteTkvChild(tgvActions, tier.cardId, angebotsId);
    return null;
  }
  return angebotsId;
}

function* currentTkvTier(cardId: string): SagaIterator<TgvTier | undefined> {
  const { tiergruppe }: IAppState = yield select();
  return tiergruppe.tiere.find(it => it.cardId === cardId && it.umfang.tkv);
}

type TkvChildOutcome = { angebotsId: string; tier: TgvTier } | { proceed: boolean };

function* recheckAfterAck(
  tier: TgvTier,
  angebotsId: string,
  context: ZumAngebotContext,
  created: CreatedInRun,
): SagaIterator<TkvChildOutcome> {
  const { tgvActions } = context;
  const after: IAppState = yield select();
  if (after.tiergruppe.angebotsIds[tier.cardId] !== angebotsId) {
    created.delete(angebotsId);
    if (
      sharedDispatcher().findStoreRef(App.TKV_V2, angebotsId) !== undefined &&
      !after.transientState.children.pendingDeletion.includes(angebotsId)
    ) {
      yield* markAndDeleteKeys([angebotsId]);
    }
    return { proceed: false };
  }
  if (after.transientState.children.pendingDeletion.includes(angebotsId)) {
    created.delete(angebotsId);
    return { proceed: false };
  }
  if (yield* hasUnusableInputs(tgvActions, after)) {
    return { proceed: false };
  }
  if (yield* hasForeignThhRow(tgvActions, after)) {
    return { proceed: false };
  }
  const wanted = yield* currentTkvTier(tier.cardId);
  if (!wanted) {
    yield* deleteTkvChild(tgvActions, tier.cardId, angebotsId);
    created.delete(angebotsId);
    return { proceed: true };
  }
  created.delete(angebotsId);
  return { angebotsId, tier: wanted };
}

function* reuseOrCreateTkvChild(
  tier: TgvTier,
  context: ZumAngebotContext,
  created: CreatedInRun,
): SagaIterator<TkvChildOutcome> {
  const { dispatcher, tgvActions } = context;
  const { tiergruppe, transientState }: IAppState = yield select();
  const listed = tiergruppe.angebotsIds[tier.cardId];
  const reusable =
    listed !== undefined &&
    !transientState.children.pendingDeletion.includes(listed) &&
    dispatcher.findStoreRef(App.TKV_V2, listed) !== undefined;
  if (reusable) {
    return { angebotsId: listed, tier };
  }
  if (listed !== undefined) {
    yield put(tgvActions.angebotsIdCleared(tier.cardId));
  }
  const angebotsId = yield* createTkvChild(tier, context, created);
  if (angebotsId === null) {
    return { proceed: yield* endWithMessage(tgvActions, 'creationFailed') };
  }
  return yield* recheckAfterAck(tier, angebotsId, context, created);
}

function* syncTkvOffer(
  plannedTier: TgvTier,
  context: ZumAngebotContext,
  created: CreatedInRun,
  healthStatusAlreadyAsked: (tierart: TierartEnum) => boolean,
): SagaIterator<boolean> {
  const { dispatcher, globalActions, tgvActions, katalog } = context;
  const present = yield* currentTkvTier(plannedTier.cardId);
  if (!present) {
    return true;
  }
  const outcome = yield* reuseOrCreateTkvChild(present, context, created);
  if ('proceed' in outcome) {
    return outcome.proceed;
  }
  const { angebotsId, tier } = outcome;
  const current = findStoreState(App.TKV_V2, angebotsId);
  if (!current) {
    return yield* endWithMessage(tgvActions, 'creationFailed');
  }
  const produktKept = keepsTkvV2Produkt(current, tier);
  dispatcher.dispatch(
    globalActions.replaceState(toTkvV2State(current, tier, katalog), App.TKV_V2, angebotsId),
  );
  dispatcher.dispatch(globalActions.createStreckeSnapshot({ appId: App.TKV_V2, angebotsId }));
  if (!produktKept) {
    dispatcher.dispatch(
      globalActions.produktRequested({
        appId: App.TKV_V2,
        produkttabelle: tkvProdukttabelle(tier.tierart),
        angebotsId,
        catalogsProvided: true,
        ...(healthStatusAlreadyAsked(tier.tierart) ? { healthStatusLoaded: true } : {}),
      }),
    );
  }
  return true;
}

function* syncThhOffer(
  offer: ThhOffer,
  context: ZumAngebotContext,
  created: CreatedInRun,
): SagaIterator<boolean> {
  const { key, app, tierart, toState } = offer;
  const { dispatcher, globalActions, tgvActions, angebotsdatum } = context;
  const { tiergruppe, transientState }: IAppState = yield select();
  const { katalog } = transientState;
  const animals = tiergruppe.tiere.filter(it => it.tierart === tierart && it.umfang.thh);
  if (animals.length === 0) {
    return true;
  }
  const repeated = tiergruppe.thhAngelegt[key];
  const vorvertrag = tiergruppe.thhVorvertrag?.[key] ?? null;
  if (!repeated) {
    yield put(tgvActions.thhAngelegt(key));
    yield put(tgvActions.childCreatingAdded(app));
    created.set(app, { thh: key });
    dispatcher.dispatch(
      vorvertrag
        ? globalActions.addVertragToAngebote({
            ...vorvertrag,
            appId: app,
            angebotsdatum,
            ownerAppId: App.TGV,
          })
        : globalActions.addProduktToAngebote(app, angebotsdatum, undefined, App.TGV),
    );
    yield put(tgvActions.childCreatingRemoved(app));
    created.delete(app);
    if (warenkorbRowOf(app)?.ownerAppId !== App.TGV) {
      yield put(tgvActions.thhCleared(key));
      return yield* endWithMessage(tgvActions, 'creationFailed');
    }
  }
  const current = findStoreState(app);
  if (!current) {
    return yield* endWithMessage(tgvActions, 'creationFailed');
  }
  const produktKept = repeated && hasLoadedProdukt(current);
  dispatcher.dispatch(
    globalActions.replaceState(
      toState(current, animals, tiergruppe.vorschaeden[key], katalog, repeated, vorvertrag),
      app,
    ),
  );
  dispatcher.dispatch(globalActions.createStreckeSnapshot({ appId: app }));
  if (!produktKept) {
    dispatcher.dispatch(globalActions.produktRequested({ appId: app, catalogsProvided: true }));
  }
  return true;
}

function* hasForeignThhRow(tgvActions: TgvActions, state: IAppState): SagaIterator<boolean> {
  const { tiergruppe } = state;
  for (const { key, app, tierart } of THH_OFFERS) {
    const needed = tiergruppe.tiere.some(it => it.tierart === tierart && it.umfang.thh);
    const row = warenkorbRowOf(app);
    if (needed && row && !(row.ownerAppId === App.TGV && tiergruppe.thhAngelegt[key])) {
      yield put(tgvActions.runFailed('thhGuard'));
      return true;
    }
  }
  return false;
}

const isThhCatalogMissing = ({ tiergruppe, transientState }: IAppState): boolean =>
  (hasAnimalOfType(tiergruppe, TierartEnum.HUND) &&
    transientState.katalog.thhDogBreeds.length === 0) ||
  (hasAnimalOfType(tiergruppe, TierartEnum.PFERD) &&
    transientState.katalog.thhHorseBreeds.length === 0);

function* hasUnusableInputs(tgvActions: TgvActions, state: IAppState): SagaIterator<boolean> {
  if (!isFormValid(state.tiergruppe, animalValidationContext(state.transientState))) {
    return true;
  }
  if (!isThhCatalogMissing(state)) {
    return false;
  }
  yield put(tgvActions.katalogRequested());
  yield put(tgvActions.runFailed('catalogMissing'));
  return true;
}

function* runCreation(created: CreatedInRun): SagaIterator<boolean> {
  const tgvActions = new TgvActions();
  const initial: IAppState = yield select();
  if (yield* hasUnusableInputs(tgvActions, initial)) {
    return false;
  }
  const state: IAppState = yield* settlePendingDeletion(tgvActions);
  if (yield* hasUnusableInputs(tgvActions, state)) {
    return false;
  }
  const context: ZumAngebotContext = {
    dispatcher: sharedDispatcher(),
    globalActions: new GlobalActions(),
    tgvActions,
    angebotsdatum: angebotsdatumOfShop(),
    katalog: state.transientState.katalog,
  };
  if (yield* hasForeignThhRow(tgvActions, state)) {
    return false;
  }
  const healthStatusAlreadyAsked = healthStatusAsker();
  for (const tier of state.tiergruppe.tiere.filter(it => it.umfang.tkv)) {
    const proceed = yield* syncTkvOffer(tier, context, created, healthStatusAlreadyAsked);
    if (!proceed) {
      return false;
    }
  }
  yield call(deferOrphanedOffersSaga);
  for (const offer of THH_OFFERS) {
    const proceed = yield* syncThhOffer(offer, context, created);
    if (!proceed) {
      return false;
    }
  }
  return true;
}

function* deleteCreatedChildren(created: CreatedInRun): SagaIterator {
  const tgvActions = new TgvActions();
  yield* markAndDeleteKeys([...created.keys()]);
  for (const [key, child] of created) {
    if (child.cardId !== undefined) {
      yield put(tgvActions.angebotsIdCleared(child.cardId));
    } else {
      yield put(tgvActions.thhCleared(child.thh));
    }
    yield put(tgvActions.childCreatingRemoved(key));
  }
  created.clear();
}

function* creationRun(): SagaIterator {
  const created: CreatedInRun = new Map();
  try {
    const completed: boolean = yield* runCreation(created);
    if (completed) {
      sharedDispatcher().dispatch(
        new GlobalActions().navigationRequested({
          appId: App.TGV,
          subroute: AppSubroute.TAA_ANGEBOT,
        }),
      );
    }
  } finally {
    if (created.size > 0) {
      yield* deleteCreatedChildren(created);
    }
  }
}

export function* zumAngebotSaga(): SagaIterator {
  let restart = true;
  while (restart) {
    const takeOverRunning: boolean = yield select(selectTakeOverRunning);
    if (takeOverRunning) {
      return;
    }
    const outcome = yield race({
      done: call(creationRun),
      clicked: take(TgvActions.TGV_ZUM_ANGEBOT_CLICKED),
      leftToDashboard: take(leavesToDashboard),
      restored: take(restoresTgvSnapshot),
    });
    restart = outcome.clicked !== undefined;
  }
}
